import { modelState } from '@/utils/modelState';
import { BookingPeriodConditionTypes } from './BookingPeriodConditionTypes/BookingPeriodConditionType';
import { QueryBuilder } from './QueryBuilder';
import { ScheduleConditionComponent } from './ScheduleConditionComponent';
import { v4 as uuidv4 } from 'uuid';
import { useModelState } from '@/utils/useModelState';
import { DateBookingPeriodConditionTypeComponent } from './DateBookingPeriodConditionTypeComponent';
import { YearlyDateBookingPeriodConditionTypeComponent } from './YearlyDateBookingPeriodConditionTypeComponent';
import { MonthsBookingPeriodConditionTypeComponent } from './MonthsBookingPeriodConditionTypeComponent';

export namespace ValidityPeriodCondition {

    export const registryEntry: QueryBuilder.ConditionRegistryEntry = {
        key: "ValidityPeriod",
        name: "Validity Period",
        isCondition: isValidityPeriodCondition,
        create: createValidityPeriodCondition,
        hydrate: hydrateValidityPeriodCondition,
        serialize: serializeValidityPeriodCondition,
        deserialize: deserializeValidityPeriodCondition,
        renderComponent: (condition: ValidityPeriodCondition) => (<ValidityPeriodConditionComponent condition={condition} />)
    };

    export const ValidityPeriodCondition = Symbol("ValidityPeriodCondition");
    export interface ValidityPeriodCondition extends QueryBuilder.ConditionNodeChild {
        [ValidityPeriodCondition]: boolean;
        conditionType: modelState<BookingPeriodConditionTypes.BookingPeriodConditionType>;
    }

    export function isValidityPeriodCondition(condition?: QueryBuilder.ConditionNodeChild): condition is ValidityPeriodCondition {
        if (!condition) {
            return false;
        }

        return (condition as ValidityPeriodCondition)[ValidityPeriodCondition] === true;
    }

    export function createValidityPeriodCondition(skipHydration: boolean = false): ValidityPeriodCondition {

        const condition: ValidityPeriodCondition = {
            [QueryBuilder.Node]: true,
            [QueryBuilder.ConditionNodeChild]: true,
            [ValidityPeriodCondition]: true,
            id: uuidv4(),
            version: modelState(0),
            conditionType: modelState(BookingPeriodConditionTypes.ScheduleBookingPeriodConditionTypeDefinition.create()),
            dispose: () => { }
        };

        if (!skipHydration) {
            hydrateValidityPeriodCondition(condition);
        }

        return condition;
    }

    export function hydrateValidityPeriodCondition(condition: ValidityPeriodCondition, recursive: boolean = false) {


        // Condition type
        let oldConditionTypeSubscription: () => void = null;

        function subscribeConditionType() {
            if (condition.conditionType.value) {
                oldConditionTypeSubscription = condition.conditionType.value.version.subscribe(() => {
                    condition.version.setValue(condition.version.value + 1);
                });
            }
        }

        subscribeConditionType();

        const unsubscribeConditionType = condition.conditionType.subscribe(() => {
            oldConditionTypeSubscription?.();

            subscribeConditionType();

            condition.version.setValue(condition.version.value + 1);
        });

        const oldDispose = condition.dispose;

        condition.dispose = () => {
            unsubscribeConditionType();

            oldDispose();
        };

        if (recursive) {
            if (condition.conditionType.value) {
                BookingPeriodConditionTypes.getBookingPeriodConditionTypeDefinition(condition.conditionType.value).hydrate(condition.conditionType.value, true);
            }
        }
    }

    function serializeValidityPeriodCondition(condition: ValidityPeriodCondition): QueryBuilder.ConditionChildNodeDtoWrapper {
        const conditionDto: ValidityPeriodConditionDto = {
            NodeId: condition.id,
            ConditionType: BookingPeriodConditionTypes.serializeBookingPeriodConditionType(condition.conditionType.value)
        };

        return {
            Type: "ValidityPeriod",
            Value: conditionDto
        };
    }

    interface ValidityPeriodConditionDto extends QueryBuilder.NodeDto {
        ConditionType: BookingPeriodConditionTypes.BookingPeriodConditionTypeDto;
    }

    function deserializeValidityPeriodCondition(conditionDto: ValidityPeriodConditionDto): ValidityPeriodCondition {
        const condition = createValidityPeriodCondition(true);

        condition.conditionType.setValue(BookingPeriodConditionTypes.deserializeBookingPeriodConditionType(conditionDto.ConditionType));
        condition.conditionType.commit();

        return condition;
    }

    function ValidityPeriodConditionComponent({ condition }: { condition: ValidityPeriodCondition }) {
        const [conditionType, setConditionType] = useModelState<BookingPeriodConditionTypes.BookingPeriodConditionType>(condition.conditionType)

        return (
            <div className="flex flex-row gap-2">
                <div>
                    <select className="form-control" value={BookingPeriodConditionTypes.getBookingPeriodConditionTypeDefinition(conditionType).key} onChange={e => {
                        setConditionType(BookingPeriodConditionTypes.createBookingPeriodConditionType(e.target.value));
                    }}>
                        {BookingPeriodConditionTypes.bookingPeriodConditionTypes.map(t => (
                            <option key={t.key} value={t.key}>{t.name}</option>
                        ))}
                    </select>
                </div>
                {BookingPeriodConditionTypes.isScheduleBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <ScheduleConditionComponent events={condition.conditionType.value.schedule} />
                    </div>
                )}
                {BookingPeriodConditionTypes.isDateBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <DateBookingPeriodConditionTypeComponent conditionType={condition.conditionType.value} />
                    </div>
                )}
                {BookingPeriodConditionTypes.isYearlyDateBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <YearlyDateBookingPeriodConditionTypeComponent conditionType={condition.conditionType.value} />
                    </div>
                )}
                {BookingPeriodConditionTypes.isMonthsBookingPeriodConditionType(condition.conditionType.value) && (
                    <div>
                        <MonthsBookingPeriodConditionTypeComponent conditionType={condition.conditionType.value} />
                    </div>
                )}
            </div>
        );
    }
}
